/**
 * SearchableSelect — Select con buscador integrado para listas largas.
 * Reemplaza a <TextField select> cuando la lista tiene muchos items.
 *
 * Props:
 *   label, value, onChange, options (array de { value, label }), required, fullWidth, size, disabled, placeholder
 *   sortByLabel (bool, default true) — ordena alfabéticamente por label
 */
import React, { useState } from 'react';
import {
  TextField, MenuItem, Box, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

export default function SearchableSelect({
  label,
  name,
  value,
  onChange,
  options = [],
  required = false,
  fullWidth = true,
  size = 'small',
  disabled = false,
  placeholder = 'Buscar...',
  sortByLabel = true,
  emptyLabel = 'Seleccionar...',
  helperText,
  error = false,
  sx,
}) {
  const [search, setSearch] = useState('');

  const sorted = sortByLabel
    ? [...options].sort((a, b) => (a.label || '').localeCompare(b.label || '', 'es', { sensitivity: 'base' }))
    : options;

  const filtered = search.trim()
    ? sorted.filter((o) => (o.label || '').toLowerCase().includes(search.trim().toLowerCase()))
    : sorted;

  return (
    <TextField
      select
      name={name}
      label={label}
      value={value || ''}
      onChange={onChange}
      fullWidth={fullWidth}
      size={size}
      required={required}
      disabled={disabled}
      error={error}
      helperText={helperText}
      sx={sx}
      SelectProps={{
        MenuProps: {
          PaperProps: {
            sx: { maxHeight: 360 },
          },
          autoFocus: false,
        },
        renderValue: (selected) => {
          if (!selected) return '';
          const opt = options.find((o) => o.value === selected);
          return opt ? opt.label : selected;
        },
      }}
    >
      <Box sx={{ position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1, px: 1.5, py: 1, borderBottom: '1px solid #E2E8F0' }}>
        <TextField
          autoFocus
          size="small"
          fullWidth
          placeholder={placeholder}
          value={search}
          onChange={(e) => { e.stopPropagation(); setSearch(e.target.value); }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ fontSize: 18, mr: 0.5, color: 'text.secondary' }} />,
          }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        />
      </Box>
      <MenuItem value="" disabled={required}>
        <Typography variant="caption" color="text.secondary">{emptyLabel}</Typography>
      </MenuItem>
      {filtered.length === 0 && (
        <MenuItem disabled>
          <Typography variant="caption" color="text.secondary">Sin resultados</Typography>
        </MenuItem>
      )}
      {filtered.map((o) => (
        <MenuItem key={o.value} value={o.value}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
  );
}
